/**
 * ParsingResultsPanel — embedded on the loan-file detail page.
 *
 * Shows per-document: parse_state pill, classification (predicted type
 * + confidence), OCR metadata, extracted fields, open review count.
 * Polls every 5s while the loan file is in a non-terminal parsing state.
 * Offers a "Re-run parsing" button gated on parsing.reparse.
 */

import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import {
  Sparkles, RefreshCw, AlertTriangle, CheckCircle2, FileText, Eye,
} from 'lucide-react';
import { Collapsible } from '@/acc-ui/primitives/Collapsible';
import { Badge } from '@/acc-ui/primitives/Badge';
import { Button } from '@/acc-ui/primitives/Button';
import { parsingApi } from '@/api/parsing-api';
import { usePermission } from '@/auth/use-permission';
import {
  DOCUMENT_PARSE_STATE,
  LOAN_FILE_STATE,
  PARSING_REPARSE,
  REVIEW_STATUS,
  type DocumentParseState,
  type DocumentParsingSnapshot,
} from '@loanaudit/shared';

const TERMINAL_LOAN_FILE_STATES: string[] = [
  LOAN_FILE_STATE.PARSED,
  LOAN_FILE_STATE.PARSING_FAILED,
  LOAN_FILE_STATE.REJECTED,
];

export function ParsingResultsPanel({ loanFileId }: { loanFileId: string }) {
  const qc = useQueryClient();
  const { has } = usePermission();
  const canRetrigger = has(PARSING_REPARSE);

  const query = useQuery({
    queryKey: ['parsing', 'loanFile', loanFileId],
    queryFn: () => parsingApi.getLoanFileView(loanFileId),
    refetchInterval: (q) => {
      const state = q.state.data?.state;
      if (!state) return 5000;
      return TERMINAL_LOAN_FILE_STATES.includes(state) ? false : 5000;
    },
  });

  const retrigger = useMutation({
    mutationFn: () => parsingApi.retrigger(loanFileId, { stage: 'all' }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['parsing', 'loanFile', loanFileId] });
      qc.invalidateQueries({ queryKey: ['loanFile', loanFileId] });
    },
  });

  const icon = <Sparkles className="w-4 h-4 text-brand-blue" />;

  if (query.isLoading) {
    return (
      <Collapsible title="Parsing Results" icon={icon}>
        <div className="py-6 text-center text-sm text-text-muted">
          Loading parsing results…
        </div>
      </Collapsible>
    );
  }

  const data = query.data;
  if (!data) return null;
  const openReviewCount = data.reviewItemsOpen;

  const actions = (
    <>
      {openReviewCount > 0 && (
        <Link to="/parsing/review-queue">
          <Badge tone="warning">
            {openReviewCount} open review{openReviewCount === 1 ? '' : 's'}
          </Badge>
        </Link>
      )}
      {canRetrigger && (
        <Button
          size="sm"
          variant="secondary"
          leftIcon={<RefreshCw className="w-3.5 h-3.5" />}
          onClick={() => retrigger.mutate()}
          loading={retrigger.isPending}
        >
          Re-run parsing
        </Button>
      )}
    </>
  );

  return (
    <Collapsible
      title="Parsing Results"
      icon={icon}
      count={data.documents.length}
      actions={actions}
    >
      {data.documents.length === 0 ? (
        <div className="py-8 text-center text-sm text-text-muted">
          No documents to parse.
        </div>
      ) : (
        <ul className="divide-y divide-border">
          {data.documents.map((doc) => (
            <DocumentParsingRow key={doc.documentId} doc={doc} />
          ))}
        </ul>
      )}
    </Collapsible>
  );
}

// -------------------------------------------------------------------------

function DocumentParsingRow({ doc }: { doc: DocumentParsingSnapshot }) {
  const [expanded, setExpanded] = useState(false);
  const hasExtraction =
    doc.extraction &&
    doc.extraction.fields &&
    Object.keys(doc.extraction.fields as unknown as Record<string, unknown>).length > 0;

  return (
    <li className="px-5 py-3">
      <div className="flex items-start gap-3">
        <FileText className="w-4 h-4 text-text-muted shrink-0 mt-0.5" />
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-medium text-text-primary truncate">{doc.fileName}</span>
            <ParseStateBadge state={doc.parseState} />
            {doc.classification && doc.classification.predictedType !== 'unclassified' && (
              <Badge tone="info">
                {doc.classification.predictedType}
                <span className="ml-1 opacity-70 tabular-nums">
                  {Math.round(doc.classification.confidence * 100)}%
                </span>
              </Badge>
            )}
            <ReviewOutcomeBadge stage="classify" item={doc.classification} />
            <ReviewOutcomeBadge stage="extract" item={doc.extraction} />
          </div>

          <div className="mt-1 text-caption text-text-tertiary flex items-center gap-4 flex-wrap tabular-nums">
            {doc.ocr && (
              <span>
                OCR: {doc.ocr.pageCount ?? '—'} pages
              </span>
            )}
            {doc.classification?.latencyMs != null && (
              <span>Classify: {formatMs(doc.classification.latencyMs)}</span>
            )}
            {doc.extraction?.latencyMs != null && (
              <span>Extract: {formatMs(doc.extraction.latencyMs)}</span>
            )}
            {doc.classification?.tokenInput != null &&
              doc.classification?.tokenOutput != null && (
                <span>
                  Tokens: {indianNum(doc.classification.tokenInput)}↑/
                  {indianNum(doc.classification.tokenOutput)}↓
                </span>
              )}
          </div>

          {hasExtraction && (
            <div className="mt-2">
              <button
                onClick={() => setExpanded((x) => !x)}
                className="text-caption font-medium text-brand-blue hover:underline flex items-center gap-1"
              >
                <Eye className="w-3 h-3" />
                {expanded ? 'Hide' : 'Show'} extracted fields
                <span className="text-text-muted font-normal">
                  ({Object.keys(doc.extraction!.fields as object).length})
                </span>
              </button>
              {expanded && <ExtractedFieldsTable extraction={doc.extraction!} />}
            </div>
          )}

          {doc.classification?.errorMessage && (
            <div className="mt-2 text-caption text-red-700 flex items-start gap-1">
              <AlertTriangle className="w-3.5 h-3.5 mt-0.5" />
              Classify error: {doc.classification.errorMessage}
            </div>
          )}
          {doc.extraction?.errorMessage && (
            <div className="mt-1 text-caption text-red-700 flex items-start gap-1">
              <AlertTriangle className="w-3.5 h-3.5 mt-0.5" />
              Extract error: {doc.extraction.errorMessage}
            </div>
          )}
        </div>
      </div>
    </li>
  );
}

// -------------------------------------------------------------------------

function ExtractedFieldsTable({
  extraction,
}: {
  extraction: DocumentParsingSnapshot['extraction'];
}) {
  if (!extraction) return null;
  const fields = extraction.fields as unknown as Record<
    string,
    { value: unknown; confidence: number; source?: { pageNumber?: number; clauseText?: string } }
  >;
  const rows = Object.entries(fields);
  if (rows.length === 0) {
    return <div className="mt-2 text-caption text-text-muted">No fields extracted.</div>;
  }
  return (
    <div className="mt-2 border border-border rounded-md overflow-hidden">
      <table className="w-full text-caption">
        <thead className="bg-surface-muted">
          <tr className="text-left">
            <th className="px-3 py-2 font-semibold text-text-secondary">Field</th>
            <th className="px-3 py-2 font-semibold text-text-secondary">Value</th>
            <th className="px-3 py-2 font-semibold text-text-secondary w-20">Conf.</th>
            <th className="px-3 py-2 font-semibold text-text-secondary w-16">Page</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-border">
          {rows.map(([key, v]) => (
            <tr key={key}>
              <td className="px-3 py-1.5 font-medium text-text-primary">{key}</td>
              <td className="px-3 py-1.5 text-text-secondary">{renderValue(v.value)}</td>
              <td className="px-3 py-1.5 tabular-nums"><ConfidencePill value={v.confidence} /></td>
              <td className="px-3 py-1.5 tabular-nums text-text-muted">
                {v.source?.pageNumber ?? '—'}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function renderValue(v: unknown): string {
  if (v === null || v === undefined) return '—';
  if (Array.isArray(v)) return v.map((x) => renderValue(x)).join(', ');
  if (typeof v === 'object') return JSON.stringify(v);
  if (typeof v === 'number') return indianNum(v);
  return String(v);
}

function ConfidencePill({ value }: { value: number }) {
  const pct = Math.round(value * 100);
  const tone: 'success' | 'warning' | 'danger' =
    value >= 0.85 ? 'success' : value >= 0.65 ? 'warning' : 'danger';
  return <Badge tone={tone}>{pct}%</Badge>;
}

/**
 * Review outcome for one parsing stage of a document.
 *
 * `requiresReview` is written when the item is created and never cleared, so
 * on its own it leaves a document badged "review" forever — including after an
 * auditor approved it. `reviewStatus` carries the resolution, so read that
 * first and fall back to "review" only while the item is still pending.
 */
function ReviewOutcomeBadge({
  stage,
  item,
}: {
  stage: 'classify' | 'extract';
  item: { requiresReview: boolean; reviewStatus: string | null } | null;
}) {
  if (!item?.requiresReview) return null;

  const status = item.reviewStatus;
  if (!status || status === REVIEW_STATUS.PENDING) {
    return <Badge tone="warning">{stage} · review</Badge>;
  }

  const tone =
    status === REVIEW_STATUS.REJECTED
      ? 'danger'
      : status === REVIEW_STATUS.CORRECTED
        ? 'info'
        : 'success';
  return (
    <Badge tone={tone}>
      {stage} · {REVIEW_STATUS_LABEL[status] ?? status}
    </Badge>
  );
}

const REVIEW_STATUS_LABEL: Record<string, string> = {
  pending: 'review', approved: 'approved', corrected: 'corrected', rejected: 'rejected',
};

function ParseStateBadge({ state }: { state: DocumentParseState }) {
  const config: Record<DocumentParseState, { tone: 'muted' | 'info' | 'warning' | 'success' | 'danger'; label: string; icon?: JSX.Element }> = {
    [DOCUMENT_PARSE_STATE.PENDING]:            { tone: 'muted',   label: 'pending' },
    [DOCUMENT_PARSE_STATE.OCR_QUEUED]:         { tone: 'info',    label: 'OCR queued' },
    [DOCUMENT_PARSE_STATE.OCR_RUNNING]:        { tone: 'info',    label: 'OCR running' },
    [DOCUMENT_PARSE_STATE.OCR_COMPLETE]:       { tone: 'info',    label: 'OCR done' },
    [DOCUMENT_PARSE_STATE.CLASSIFY_QUEUED]:    { tone: 'info',    label: 'classify queued' },
    [DOCUMENT_PARSE_STATE.CLASSIFY_RUNNING]:   { tone: 'info',    label: 'classifying' },
    [DOCUMENT_PARSE_STATE.CLASSIFY_COMPLETE]:  { tone: 'info',    label: 'classified' },
    [DOCUMENT_PARSE_STATE.EXTRACT_QUEUED]:     { tone: 'info',    label: 'extract queued' },
    [DOCUMENT_PARSE_STATE.EXTRACT_RUNNING]:    { tone: 'info',    label: 'extracting' },
    [DOCUMENT_PARSE_STATE.EXTRACT_COMPLETE]:   { tone: 'success', label: 'extracted' },
    [DOCUMENT_PARSE_STATE.NEEDS_REVIEW]:       { tone: 'warning', label: 'needs review' },
    [DOCUMENT_PARSE_STATE.PARSE_COMPLETE]:     { tone: 'success', label: 'parsed', icon: <CheckCircle2 className="w-3 h-3" /> },
    [DOCUMENT_PARSE_STATE.FAILED]:             { tone: 'danger',  label: 'failed', icon: <AlertTriangle className="w-3 h-3" /> },
  };
  const c = config[state] ?? { tone: 'muted' as const, label: state };
  return (
    <Badge tone={c.tone}>
      {c.icon && <span className="mr-1 inline-flex">{c.icon}</span>}
      {c.label}
    </Badge>
  );
}

// -------------------------------------------------------------------------

function indianNum(n: number): string {
  if (!Number.isFinite(n)) return String(n);
  const sign = n < 0 ? '-' : '';
  const abs = Math.abs(Math.round(n));
  const s = String(abs);
  if (s.length <= 3) return sign + s;
  const last3 = s.slice(-3);
  const rest = s.slice(0, -3);
  const grouped = rest.replace(/\B(?=(\d{2})+(?!\d))/g, ',');
  return `${sign}${grouped},${last3}`;
}

function formatMs(ms: number): string {
  if (ms < 1000) return `${ms}ms`;
  return `${(ms / 1000).toFixed(1)}s`;
}
